import shared from "../Shared/shared.module.css";
import { IconBriefcase, IconPlus, IconTrash, IconArrowRight, IconChevronsLeft } from "@tabler/icons-react";
import { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { talentWorkHistory, talentProfile } from "../../../services/talentCalls";
import { errorHandler } from "../../../utils";
import { locationSelectStyle, selectStyle } from "../../../utils/functions";
import Select from "react-select";
import FullScreenLoader from "../../../components/Loaders/FullScreen/loader";
import DatePicker from "react-datepicker";
import { industryOptions } from "../../../utils/dropdownItems";
import { UserContext } from "../../../context";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";

const DEFAULT_FORM = {
  companyName: "",
  jobTitle: "",
  location: "",
  startDate: "",
  endDate: "",
  jobType: "",
  industry: "",
  jobDescription: "",
  isCurrentJob: false,
  workCertificate: null,
};

const JOB_TYPES = ["Contract Work", "Full Time", "Internship", "Remote Work", "Part Time"];

const Stage5 = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [errors, setErrors] = useState({});
  const [load, setLoad] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forms, setForms] = useState([{ ...DEFAULT_FORM }]);
  const { GOOGLE_API_KEY } = useContext(UserContext);
  const navigate = useNavigate();

  const extractMostRecentWorkExperience = useCallback(() => {
    try {
      const extractedDataString = localStorage.getItem("extractedResumeData");
      if (!extractedDataString) return;
      const extractedData = JSON.parse(extractedDataString);
      if (!extractedData?.experience || !Array.isArray(extractedData.experience) || extractedData.experience.length === 0) return;

      const mostRecentExp = extractedData.experience[0];
      let companyName = "";
      if (mostRecentExp.company && !mostRecentExp.company.startsWith("•")) {
        companyName = mostRecentExp.company.replace("•", "").trim();
      } else if (mostRecentExp.location && !mostRecentExp.location.startsWith("•")) {
        const location = mostRecentExp.location.replace("•", "").trim();
        if (!location.includes(",") && !location.match(/\b(avenue|street|road|lane|drive|boulevard|city|town|state|province|country)\b/i)) {
          companyName = location;
        }
      }
      let jobTitle = "";
      if (mostRecentExp.title && !mostRecentExp.title.startsWith("•")) jobTitle = mostRecentExp.title.replace("•", "").trim();
      let jobDescription = "";
      if (mostRecentExp.bullets) jobDescription = mostRecentExp.bullets.map((b) => b.replace("•", "").trim()).join("\n• ");

      setForms([
        {
          companyName,
          jobTitle,
          location: "",
          startDate: null,
          endDate: null,
          jobType: "Full Time",
          industry: "Technology",
          jobDescription,
          isCurrentJob: mostRecentExp.endDate?.includes("Present") || false,
          workCertificate: null,
        },
      ]);
    } catch (_) {
      errorHandler("Failed to parse work experience data from your resume", "Error");
    }
  }, []);

  const handleChange = (e, i) => {
    const newForms = [...forms];
    newForms[i][e.target.name] = e.target.value;
    setForms(newForms);
  };

  const handleChangeLocation = (e, i) => {
    const newForms = [...forms];
    newForms[i].location = e.label;
    setForms(newForms);
  };
  const handleChangeIndustry = (e, i) => {
    const newForms = [...forms];
    newForms[i].industry = e.value;
    setForms(newForms);
  };

  const handleChangeDate = (i, name, date) => {
    const newForms = [...forms];
    newForms[i][name] = date;
    setForms(newForms);
    const newErrors = { ...errors };
    delete newErrors[`form_${i}_${name}`];
    setErrors(newErrors);
  };

  const submit = async () => {
    setLoad(true);
    try {
      const res = await talentWorkHistory({ workHistoryExperience: forms });
      if (res?.data) navigate({ pathname: "/talent-onboarding", search: "stage=6" });
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Something went wrong, Please check your internet connection and try again.", "Error");
    } finally {
      setLoad(false);
    }
  };

  const getUserProfile = async () => {
    setLoading(true);
    try {
      const res = await talentProfile();
      if (res?.data?.data?.talent) {
        if (res?.data?.data?.talent?.workHistoryExperience?.length > 0) {
          setForms(res?.data?.data?.talent?.workHistoryExperience);
        }
      }
    } catch (_) {
      errorHandler("Failed to fetch your profile information", "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    extractMostRecentWorkExperience();
    getUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractMostRecentWorkExperience]);

  const addForm = () => forms.length < 5 && setForms([...forms, { ...DEFAULT_FORM }]);
  const removeForm = (index) => {
    if (forms.length > 1) {
      const updated = [...forms];
      updated.splice(index, 1);
      setForms(updated);
    }
  };

  return (
    <>
      {(loading || load) && <FullScreenLoader />}
      <div className={shared.panelHero}>
        <div className={shared.panelHeroLeft}>
          <div className={shared.panelHeroIcon}>
            <IconBriefcase size={22} />
          </div>
          <div>
            <div className={shared.panelTitle}>Work History &amp; Experience</div>
            <div className={shared.panelSubtitle}>Share your professional experience</div>
          </div>
        </div>
        {forms.length < 5 && (
          <div className={shared.panelHeroActionWrap}>
            <div className={shared.panelHeroAction} title="Add another role" onClick={addForm}>
              <IconPlus size={18} />
            </div>
            <div className={shared.panelHeroActionLabel}>Add Role</div>
          </div>
        )}
      </div>

      <div className={shared.panelBody}>
        {forms.map((item, index) => (
          <div className={shared.entryBlock} key={index}>
            {index > 0 && (
              <button type="button" className={shared.btnRemoveEntry} onClick={() => removeForm(index)}>
                <IconTrash size={12} /> Remove
              </button>
            )}

            <div className={shared.toggleRow} style={{ marginBottom: 16 }}>
              <label className={shared.toggleSwitch}>
                <input
                  type="checkbox"
                  checked={item.isCurrentJob}
                  onChange={() => {
                    const newForms = [...forms];
                    newForms[index].isCurrentJob = !newForms[index].isCurrentJob;
                    setForms(newForms);
                  }}
                />
                <span className={shared.toggleSlider} />
              </label>
              <span className={shared.toggleLabel}>Currently working here</span>
            </div>

            <div className={`${shared.formRow} ${shared.formRowFull}`}>
              <div className={shared.formField}>
                <label className={shared.formLabel}>Company Name</label>
                <input className={shared.formInput} type="text" placeholder="Company name" name="companyName" value={item.companyName} onChange={(e) => handleChange(e, index)} />
                {errors[`form_${index}_companyName`] && <div className={shared.fieldErr}>{errors[`form_${index}_companyName`]}</div>}
              </div>
            </div>

            <div className={shared.formRow}>
              <div className={shared.formField}>
                <label className={shared.formLabel}>Job Title</label>
                <input className={shared.formInput} type="text" placeholder="Job title" name="jobTitle" value={item.jobTitle} onChange={(e) => handleChange(e, index)} />
              </div>
              <div className={shared.formField}>
                <label className={shared.formLabel}>Location</label>
                <GooglePlacesAutocomplete
                  apiKey={GOOGLE_API_KEY}
                  selectProps={{
                    styles: locationSelectStyle,
                    name: "location",
                    placeholder: "Type in your location...",
                    value: item.location ? { label: item.location, value: item.location } : null,
                    onChange: (e) => handleChangeLocation(e, index),
                    isSearchable: true,
                    onKeyDown: (e) => e.key === "Enter" && e.preventDefault(),
                  }}
                />
              </div>
            </div>

            <div className={shared.formRow}>
              <div className={shared.formField}>
                <label className={shared.formLabel}>Start Date</label>
                <DatePicker
                  selected={item.startDate ? new Date(item.startDate) : null}
                  dateFormat="dd-MM-yyyy"
                  placeholderText="dd-mm-yyyy"
                  showMonthDropdown
                  useShortMonthInDropdown
                  showYearDropdown
                  maxDate={item.endDate ? new Date(item.endDate) : new Date()}
                  onChange={(date) => handleChangeDate(index, "startDate", date)}
                  className={shared.formInput}
                  wrapperClassName="w-full"
                />
                {errors[`form_${index}_startDate`] && <div className={shared.fieldErr}>{errors[`form_${index}_startDate`]}</div>}
              </div>
              <div className={shared.formField}>
                <label className={shared.formLabel}>End Date</label>
                <DatePicker
                  disabled={item.isCurrentJob}
                  selected={item.endDate ? new Date(item.endDate) : null}
                  dateFormat="dd-MM-yyyy"
                  placeholderText="dd-mm-yyyy"
                  showMonthDropdown
                  useShortMonthInDropdown
                  showYearDropdown
                  minDate={item.startDate ? new Date(item.startDate) : null}
                  maxDate={new Date()}
                  onChange={(date) => handleChangeDate(index, "endDate", date)}
                  className={shared.formInput}
                  wrapperClassName="w-full"
                />
                {errors[`form_${index}_endDate`] && <div className={shared.fieldErr}>{errors[`form_${index}_endDate`]}</div>}
              </div>
            </div>

            <div className={shared.formRow}>
              <div className={shared.formField}>
                <label className={shared.formLabel}>Job Type</label>
                <select className={shared.formSelect} name="jobType" value={item.jobType} onChange={(e) => handleChange(e, index)}>
                  <option value="">Choose job type</option>
                  {JOB_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className={shared.formField}>
                <label className={shared.formLabel}>Industry</label>
                <Select
                  styles={selectStyle}
                  name="industry"
                  options={industryOptions}
                  value={industryOptions?.find((o) => o.value === item.industry)}
                  onChange={(e) => handleChangeIndustry(e, index)}
                  isSearchable
                />
              </div>
            </div>

            <div className={`${shared.formRow} ${shared.formRowFull}`}>
              <div className={shared.formField}>
                <label className={shared.formLabel}>Job Description</label>
                <textarea
                  className={shared.formTextarea}
                  name="jobDescription"
                  placeholder="Describe your responsibilities and achievements"
                  value={item.jobDescription}
                  onChange={(e) => handleChange(e, index)}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={shared.panelFooter}>
        <button type="button" className={shared.btnBack} onClick={() => navigate("?stage=4")}>
          <IconChevronsLeft size={16} /> Back
        </button>
        <div className={shared.footerActions}>
          <button type="button" className={shared.btnPrimary} onClick={submit} disabled={load}>
            Next <IconArrowRight size={15} />
          </button>
        </div>
      </div>
    </>
  );
};

export default Stage5;
